import { CommerceApiConfig } from '~/lib/data-contract/salesforce';
import { useConfig } from '~/shared/hooks';

/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

// Helper function to check if URL is preprod
const isPreprodDomain = (url: string) => url.includes('preprod01');

export const commerceAPIConfig: () => CommerceApiConfig = () => {
    const { config } = useConfig();

    // Determine if we're in preprod environment using window
    const isPreprod = typeof window !== 'undefined' && isPreprodDomain(window.location.hostname);

    // Use the current domain for requests if in preprod
    const baseUrl = isPreprod ? `https://${window.location.hostname}` : config.NEXT_PUBLIC_URL;

    return {
        proxy: `/api/salesforce/api`,
        parameters: {
            clientId: config.NEXT_PUBLIC_SALESFORCE_CLIENT_ID,
            accountClientId: config.NEXT_PUBLIC_SALESFORCE_CLIENT_ACCOUNTAPI_CLIENTID,
            organizationId: config.NEXT_PUBLIC_SALESFORCE_ORGANIZATION_ID,
            shortCode: config.NEXT_PUBLIC_SALESFORCE_SHORTCODE,
            siteId: config.NEXT_PUBLIC_SALESFORCE_SITE_ID,
            /**
             * NOTE: At the moment, we are using the same value as siteId for our channelId.
             * At some point, we might have to introduce a separate environment variable for it.
             */
            channelId: config.NEXT_PUBLIC_SALESFORCE_SITE_ID,
            publicUrl: baseUrl,
        },
        ocapiHost: baseUrl + '/api/salesforce/ocapi',
    };
};

export const commerceServerAPIConfig: () => CommerceApiConfig = () => {
    const { serverConfig } = useConfig();

    // For server-side, check the request headers
    const headers =
        typeof window === 'undefined'
            ? // @ts-ignore - assuming we have access to headers in server context
              (global as any).__NEXT_HEADERS
            : null;

    const host = headers?.get('host') || '';
    const isPreprod = isPreprodDomain(host);

    // Use appropriate URL based on hostname
    const baseUrl = isPreprod ? `https://${host}` : serverConfig.NEXT_PUBLIC_URL;

    return {
        proxy: `/api/salesforce/api`,
        parameters: {
            clientId: serverConfig.NEXT_PUBLIC_SALESFORCE_CLIENT_ID,
            accountClientId: serverConfig.NEXT_PUBLIC_SALESFORCE_CLIENT_ACCOUNTAPI_CLIENTID,
            organizationId: serverConfig.NEXT_PUBLIC_SALESFORCE_ORGANIZATION_ID,
            shortCode: serverConfig.NEXT_PUBLIC_SALESFORCE_SHORTCODE,
            siteId: serverConfig.NEXT_PUBLIC_SALESFORCE_SITE_ID,
            /**
             * NOTE: At the moment, we are using the same value as siteId for our channelId.
             * At some point, we might have to introduce a separate environment variable for it.
             */
            channelId: serverConfig.NEXT_PUBLIC_SALESFORCE_SITE_ID,
            publicUrl: baseUrl,
        },
        ocapiHost: baseUrl + '/api/salesforce/ocapi',
    } as CommerceApiConfig;
};
